import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableCellNode,
  $isTableNode,
  TableCellNode,
  TableNode,
} from "@lexical/table";
import { Add, DeleteOutline } from "@mui/icons-material";
import { $getNearestNodeFromDOMNode, $getNodeByKey, LexicalNode } from "lexical";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOptionalAutosaveContext } from "../../AutoSavePlugin";
import "./TableActionMenuPlugin.css";

// Padding from viewport edges
const VIEWPORT_PADDING = 8;

interface MenuState {
  isOpen: boolean;
  x: number;
  y: number;
  tableNodeKey: string;
  cellNodeKey: string;
}

/**
 * Calculates menu position adjusted to stay within viewport bounds.
 */
const getAdjustedMenuPosition = (
  x: number,
  y: number,
  menuWidth: number,
  menuHeight: number
): { x: number; y: number } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let adjustedX = x;
  let adjustedY = y;

  // Adjust horizontal position if menu would overflow right edge
  if (x + menuWidth + VIEWPORT_PADDING > viewportWidth) {
    adjustedX = viewportWidth - menuWidth - VIEWPORT_PADDING;
  }

  // Adjust horizontal position if menu would overflow left edge
  if (adjustedX < VIEWPORT_PADDING) {
    adjustedX = VIEWPORT_PADDING;
  }

  // Adjust vertical position if menu would overflow bottom edge
  if (y + menuHeight + VIEWPORT_PADDING > viewportHeight) {
    adjustedY = viewportHeight - menuHeight - VIEWPORT_PADDING;
  }

  // Adjust vertical position if menu would overflow top edge
  if (adjustedY < VIEWPORT_PADDING) {
    adjustedY = VIEWPORT_PADDING;
  }

  return { x: adjustedX, y: adjustedY };
};

function TableActionMenuPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [menuState, setMenuState] = useState<MenuState | null>(null);
  const [adjustedPosition, setAdjustedPosition] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { triggerAutosave } = useOptionalAutosaveContext();

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      if (!editor.isEditable()) {
        return;
      }

      const target = event.target as HTMLElement;
      const cell = target.closest("th, td") as HTMLTableCellElement | null;

      // Only show menu for table cells
      if (!cell) {
        return;
      }

      event.preventDefault();

      editor.update(() => {
        const cellNode = $getNearestNodeFromDOMNode(cell);
        if (!cellNode) return;

        // Walk up to find table node and cell node
        let current: LexicalNode | null = cellNode;
        let tableNode: TableNode | null = null;
        let tableCellNode: TableCellNode | null = null;

        while (current) {
          if ($isTableCellNode(current) && !tableCellNode) {
            tableCellNode = current;
          }
          if ($isTableNode(current)) {
            tableNode = current;
            break;
          }
          current = current.getParent();
        }

        if (!tableNode || !tableCellNode) return;

        // Reset adjusted position when opening menu
        setAdjustedPosition(null);
        setMenuState({
          isOpen: true,
          x: event.clientX,
          y: event.clientY,
          tableNodeKey: tableNode.getKey(),
          cellNodeKey: tableCellNode.getKey(),
        });
      });
    },
    [editor]
  );

  const closeMenu = useCallback(() => {
    setMenuState(null);
    setAdjustedPosition(null);
  }, []);

  const handleDeleteRow = useCallback(() => {
    if (!menuState) return;

    editor.update(() => {
      const cellNode = $getNodeByKey(menuState.cellNodeKey);
      if (!$isTableCellNode(cellNode)) return;

      // Select the cell first to set context
      cellNode.selectEnd();

      // Delete the row
      $deleteTableRow__EXPERIMENTAL();
    });

    // Trigger autosave after row deletion
    triggerAutosave();

    closeMenu();
  }, [editor, menuState, closeMenu, triggerAutosave]);

  const handleAddRow = useCallback(() => {
    if (!menuState) return;

    editor.update(() => {
      const cellNode = $getNodeByKey(menuState.cellNodeKey);
      if (!$isTableCellNode(cellNode)) return;

      // Select the cell first to set context
      cellNode.selectEnd();

      // Insert row below (true = insertAfter)
      try {
        $insertTableRow__EXPERIMENTAL(true);
      } catch (error) {
        console.error("Failed to insert table row:", error);
      }
    });

    // Trigger autosave after row insertion
    triggerAutosave();

    closeMenu();
  }, [editor, menuState, closeMenu, triggerAutosave]);

  const handleDeleteColumn = useCallback(() => {
    if (!menuState) return;

    editor.update(() => {
      const cellNode = $getNodeByKey(menuState.cellNodeKey);
      if (!$isTableCellNode(cellNode)) return;

      // Select the cell first to set context
      cellNode.selectEnd();

      // Delete the column
      $deleteTableColumn__EXPERIMENTAL();
    });

    // Trigger autosave after column deletion
    triggerAutosave();

    closeMenu();
  }, [editor, menuState, closeMenu, triggerAutosave]);

  const handleAddColumn = useCallback(() => {
    if (!menuState) return;

    editor.update(() => {
      const cellNode = $getNodeByKey(menuState.cellNodeKey);
      if (!$isTableCellNode(cellNode)) return;

      // Select the cell first to set context
      cellNode.selectEnd();

      // Insert column to the right (true = insertAfter)
      try {
        $insertTableColumn__EXPERIMENTAL(true);
      } catch (error) {
        console.error("Failed to insert table column:", error);
      }
    });

    // Trigger autosave after column insertion
    triggerAutosave();

    closeMenu();
  }, [editor, menuState, closeMenu, triggerAutosave]);

  useEffect(() => {
    const editorRoot = editor.getRootElement();
    if (!editorRoot) return;

    editorRoot.addEventListener("contextmenu", handleContextMenu);

    return () => {
      editorRoot.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [editor, handleContextMenu]);

  useEffect(() => {
    if (!menuState?.isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".table-action-menu")) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
        return;
      }

      // Keyboard navigation for menu items
      const menu = menuRef.current;
      if (!menu) return;

      const menuItems = menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
      const currentIndex = Array.from(menuItems).findIndex(
        (item) => item === document.activeElement
      );

      if (event.key === "ArrowDown") {
        event.preventDefault();
        const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
        menuItems[nextIndex]?.focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
        menuItems[prevIndex]?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    // Focus the first menu item when menu opens
    setTimeout(() => {
      const firstItem = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
      firstItem?.focus();
    }, 0);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuState?.isOpen, closeMenu]);

  // Adjust menu position after render to keep it within viewport
  useLayoutEffect(() => {
    if (!menuState?.isOpen || !menuRef.current) return;

    const menuRect = menuRef.current.getBoundingClientRect();
    const adjusted = getAdjustedMenuPosition(
      menuState.x,
      menuState.y,
      menuRect.width,
      menuRect.height
    );

    // Only update if position actually changed
    if (adjusted.x !== menuState.x || adjusted.y !== menuState.y) {
      setAdjustedPosition(adjusted);
    }
  }, [menuState]);

  if (!menuState?.isOpen) {
    return null;
  }

  // Use adjusted position if available, otherwise use initial click position
  const position = adjustedPosition || { x: menuState.x, y: menuState.y };

  return createPortal(
    <div
      ref={menuRef}
      className="table-action-menu"
      role="menu"
      aria-label="Table actions"
      style={{
        position: "fixed",
        top: position.y,
        left: position.x,
      }}
    >
      <button
        className="table-action-menu-item table-action-menu-item-add-row"
        onClick={handleAddRow}
        type="button"
        role="menuitem"
        aria-label="Add row"
      >
        <Add />
        Add Row
      </button>
      <button
        className="table-action-menu-item table-action-menu-item-add-column"
        onClick={handleAddColumn}
        type="button"
        role="menuitem"
        aria-label="Add column"
      >
        <Add />
        Add Column
      </button>
      <button
        className="table-action-menu-item table-action-menu-item-delete-row"
        onClick={handleDeleteRow}
        type="button"
        role="menuitem"
        aria-label="Delete row"
      >
        <DeleteOutline />
        Delete Row
      </button>
      <button
        className="table-action-menu-item table-action-menu-item-delete-column"
        onClick={handleDeleteColumn}
        type="button"
        role="menuitem"
        aria-label="Delete column"
      >
        <DeleteOutline />
        Delete Column
      </button>
    </div>,
    document.body
  );
}

export default TableActionMenuPlugin;
